import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../lib/auth';
import { useState, useEffect } from 'react';
import { useRecipes } from '../hooks/useRecipes';
import { useKitchen } from '../hooks/useKitchen';
import { useToast } from '../hooks/useToast';
import { useAction as useConvexAction, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import clsx from 'clsx';

export const Route = createFileRoute('/recipes/')({
  component: RecipesPage,
});

type ImportTab = 'url' | 'photo';

type ImportedPreview = {
  title: string;
  description?: string;
  imageUrl?: string;
  servings?: number;
  prepTime?: number;
  cookTime?: number;
  totalTime?: number;
  ingredients: { name: string; amount?: string; unit?: string }[];
  steps: { instruction: string; ingredientIndices?: number[] }[];
  source?: 'jsonld' | 'html' | 'ai';
  confidence?: number;
  warnings?: string[];
};

function RecipesPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const { toasts, success, error: showError } = useToast();
  const { kitchen, createKitchen, isLoading: kitchenLoading } = useKitchen();
  const [isImporting, setIsImporting] = useState(false);
  const [importTab, setImportTab] = useState<ImportTab>('url');
  const [importUrl, setImportUrl] = useState('');
  const [isImportingUrl, setIsImportingUrl] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportedPreview | null>(null);
  const [isSavingImport, setIsSavingImport] = useState(false);
  
  // Auto-create kitchen if none exists
  useEffect(() => {
    if (isLoaded && isSignedIn && !kitchenLoading && kitchen === null) {
      createKitchen().catch(() => {
        // Silent fail
      });
    }
  }, [isLoaded, isSignedIn, kitchenLoading, kitchen, createKitchen]);
  
  // Redirect if not signed in
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/' });
    }
  }, [isLoaded, isSignedIn, navigate]);
  
  const { recipes, isLoading, deleteRecipe: deleteRecipeConvex } = useRecipes();
  
  // Convex mutations
  const scrapeMutation = useConvexAction(api.recipeImport.importFromUrl);
  const saveImportedMutation = useConvexMutation(api.recipeImport.saveImported);

  const handleImport = async () => {
    let url = importUrl.trim();

    if (!url) {
      showError('Please enter a recipe URL');
      return;
    }

    try {
      if (!url.match(/^https?:\/\//i)) {
        url = 'https://' + url;
      }
      new URL(url);
    } catch {
      showError('Please enter a valid URL (e.g., https://example.com/recipe)');
      return;
    }

    const domainMatch = url.match(/^https?:\/\/([^/]+)/i);
    const domain = domainMatch ? domainMatch[1] : '';
    if (!domain || !domain.includes('.') || !domain.match(/\.[a-z]{2,}$/i)) {
      showError('Please enter a complete URL (e.g., https://joshuaweissman.com/recipes/bagels)');
      return;
    }

    setIsImportingUrl(true);

    try {
      const scraped = await scrapeMutation({ url });
      setImportPreview(scraped as ImportedPreview);
    } catch (err: any) {
      console.error('[Recipe Import] Error:', err);
      showError(err.message || 'Failed to import recipe');
    } finally {
      setIsImportingUrl(false);
    }
  };

  const handleSaveImported = async () => {
    if (!importPreview) return;

    setIsSavingImport(true);
    try {
      const saveArgs: Record<string, any> = { sourceUrl: importUrl.trim() };
      for (const key of ['title', 'description', 'ingredients', 'steps', 'servings', 'prepTime', 'cookTime', 'totalTime', 'imageUrl']) {
        if ((importPreview as any)[key] !== undefined) saveArgs[key] = (importPreview as any)[key];
      }
      await saveImportedMutation(saveArgs as any);
      success(`Imported "${importPreview.title}"!`);
      setImportPreview(null);
      setImportUrl('');
      setIsImporting(false);
    } catch (err: any) {
      showError(err.message || 'Failed to save imported recipe');
    } finally {
      setIsSavingImport(false);
    }
  };
  
  const deleteRecipe = async (id: string) => {
    await deleteRecipeConvex(id);
  };
  
  if (!isLoaded) {
    return <div className="flex items-center justify-center min-h-[50vh]">Loading...</div>;
  }
  
  if (!isSignedIn) {
    return null;
  }
  
  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="section-title">Your Recipes</h1>
          <p className="section-subtitle">
            {recipes?.length || 0} recipes in your collection
          </p>
        </div>
        
        <button
          onClick={() => { setImportPreview(null); setIsImporting(true); }}
          className="btn btn-primary"
        >
          <span>✨</span>
          Import Recipe
        </button>
      </div>
      
      {/* Import Modal */}
      {isImporting && (
        <div className="fixed inset-0 bg-warm-900/50 flex items-start justify-center z-50 p-4 animate-fade-in overflow-y-auto">
          <div className="card p-6 w-full max-w-2xl my-8 animate-scale-in">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => { setIsImporting(false); setImportUrl(''); setImportPreview(null); }}
                className="text-stone hover:text-ink transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-coral-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📖</span>
              </div>
              <h2 className="font-display text-2xl font-semibold text-ink mb-2">
                Import Recipe
              </h2>
              <p className="text-stone mb-6">
                Paste a recipe URL to import
              </p>
              
              {/* Tab buttons */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setImportTab('url')}
                  className={clsx(
                    'flex-1 p-4 rounded-xl border-2 transition-all',
                    importTab === 'url'
                      ? 'border-coral-500 bg-coral-50'
                      : 'border-sand hover:border-coral-200'
                  )}
                >
                  <span className="text-3xl mb-2 block">🔗</span>
                  <span className="font-medium text-ink">From URL</span>
                </button>
                <button
                  onClick={() => setImportTab('photo')}
                  className={clsx(
                    'flex-1 p-4 rounded-xl border-2 transition-all',
                    importTab === 'photo'
                      ? 'border-coral-500 bg-coral-50'
                      : 'border-sand hover:border-coral-200'
                  )}
                >
                  <span className="text-3xl mb-2 block">📷</span>
                  <span className="font-medium text-ink">From Photo</span>
                </button>
              </div>
              
              {importTab === 'url' && (
                <div>
                  {!importPreview ? (
                    <>
                      <input
                        type="text"
                        className="input input-lg mb-4"
                        placeholder="https://example.com/delicious-recipe..."
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                        autoFocus
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setIsImporting(false); setImportUrl(''); setImportPreview(null); }}
                          className="btn btn-ghost flex-1"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleImport}
                          disabled={!importUrl || isImportingUrl}
                          className="btn btn-primary flex-1"
                        >
                          {isImportingUrl ? (
                            <>
                              <span className="animate-spin">⏳</span>
                              Importing...
                            </>
                          ) : (
                            <>
                              <span>✨</span>
                              Import
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-center text-stone text-sm mt-4">
                        Works with AllRecipes, BBC Good Food, NYT Cooking, and more
                      </p>
                    </>
                  ) : (
                    <div className="text-left space-y-4">
                      <div className="rounded-xl border border-sand p-4 bg-cream-50">
                        <h3 className="font-semibold text-ink mb-2">{importPreview.title}</h3>
                        <div className="text-sm text-stone space-y-1">
                          <p>Source: <span className="font-medium text-ink uppercase">{importPreview.source || 'unknown'}</span></p>
                          <p>Confidence: <span className="font-medium text-ink">{Math.round((importPreview.confidence ?? 0) * 100)}%</span></p>
                          <p>{importPreview.ingredients.length} ingredients • {importPreview.steps.length} steps</p>
                        </div>
                      </div>

                      {(importPreview.warnings || []).length > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          <p className="font-medium mb-1">Import warnings:</p>
                          <ul className="list-disc ml-5">
                            {(importPreview.warnings || []).map((w, i) => (
                              <li key={`${w}-${i}`}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {(importPreview.confidence ?? 0) < 0.6 && (
                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                          <strong>Please review carefully.</strong> This import has low confidence and may need edits.
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button
                          onClick={() => setImportPreview(null)}
                          className="btn btn-ghost flex-1"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleSaveImported}
                          disabled={isSavingImport}
                          className="btn btn-primary flex-1"
                        >
                          {isSavingImport ? 'Saving...' : 'Save Recipe'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {importTab === 'photo' && (
                <div>
                  <div className="p-4 bg-amber-50 rounded-lg mb-4">
                    <p className="text-amber-800 text-sm">
                      📷 Photo import coming soon! Please use URL import for now.
                    </p>
                  </div>
                  <button
                    onClick={() => setImportTab('url')}
                    className="btn btn-secondary w-full"
                  >
                    Switch to URL Import
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Recipe Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <RecipeCardSkeleton key={i} />
          ))}
        </div>
      ) : recipes?.length === 0 ? (
        <EmptyState onImport={() => { setImportPreview(null); setIsImporting(true); }} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {recipes?.map((recipe: any, index: number) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              index={index}
              onDelete={() => {
                if (confirm('Delete this recipe?')) {
                  deleteRecipe(recipe.id);
                }
              }}
            />
          ))}
        </div>
      )}
      
      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map((toast: any) => (
          <div
            key={toast.id}
            className={clsx(
              'px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-slide-in',
              toast.type === 'success' && 'bg-green-100 text-green-800 border border-green-200',
              toast.type === 'error' && 'bg-rose-100 text-rose-800 border border-rose-200'
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onImport }: { onImport: () => void }) {
  return (
    <div className="card p-12 text-center max-w-md mx-auto">
      <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-coral-100 to-coral-200 flex items-center justify-center mx-auto mb-6">
        <span className="text-5xl">📖</span>
      </div>
      <h2 className="font-display text-2xl font-semibold text-ink mb-2">
        Start Your Collection
      </h2>
      <p className="text-stone mb-6 max-w-sm mx-auto">
        Import recipes from your favorite websites and build your personal cookbook
      </p>
      <button onClick={onImport} className="btn btn-primary btn-lg">
        <span>✨</span>
        Import Your First Recipe
      </button>
    </div>
  );
}

function RecipeCardSkeleton() {
  return (
    <div className="card overflow-hidden">
      <div className="aspect-[4/3] skeleton" />
      <div className="p-4">
        <div className="skeleton h-5 w-3/4 mb-3" />
        <div className="skeleton h-4 w-1/2 mb-4" />
        <div className="flex gap-2">
          <div className="skeleton h-6 w-16 rounded-lg" />
          <div className="skeleton h-6 w-16 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function getPlaceholderGradient(title: string) {
  const gradients = [
    'from-amber-200 via-orange-200 to-rose-200',
    'from-emerald-200 via-teal-200 to-cyan-200',
    'from-violet-200 via-purple-200 to-fuchsia-200',
    'from-rose-200 via-pink-200 to-red-200',
    'from-sky-200 via-blue-200 to-indigo-200',
    'from-lime-200 via-green-200 to-emerald-200',
    'from-yellow-200 via-amber-200 to-orange-200',
    'from-fuchsia-200 via-pink-200 to-rose-200',
  ];
  const index = title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % gradients.length;
  return gradients[index];
}

function getPlaceholderEmoji(course?: string, cuisine?: string) {
  if (cuisine?.toLowerCase().includes('italian')) return '🍝';
  if (cuisine?.toLowerCase().includes('asian') || cuisine?.toLowerCase().includes('chinese') || cuisine?.toLowerCase().includes('japanese')) return '🥡';
  if (cuisine?.toLowerCase().includes('mexican')) return '🌮';
  if (cuisine?.toLowerCase().includes('indian')) return '🍛';
  
  if (course?.toLowerCase().includes('breakfast')) return '🍳';
  if (course?.toLowerCase().includes('dessert')) return '🍰';
  if (course?.toLowerCase().includes('soup')) return '🍲';
  if (course?.toLowerCase().includes('salad')) return '🥗';
  if (course?.toLowerCase().includes('drink') || course?.toLowerCase().includes('beverage')) return '🍹';
  
  const emojis = ['🍳', '🥘', '🍲', '🥗', '🍜', '🥙', '🍱', '🥧'];
  return emojis[Math.floor(Math.random() * emojis.length)];
}

function RecipeCard({ 
  recipe, 
  index,
  onDelete 
}: { 
  recipe: any; 
  index: number;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const totalTime = recipe.totalTime || (recipe.prepTime || 0) + (recipe.cookTime || 0);
  
  return (
    <div 
      className="card card-interactive overflow-hidden group"
      style={{ animationDelay: `${index * 50}ms` }}
      onClick={() => navigate({ to: '/recipes/$recipeId', params: { recipeId: recipe.id } })}
    >
      {recipe.imageUrl ? (
        <div className="aspect-[4/3] bg-sand relative overflow-hidden">
          <img
            src={recipe.imageUrl}
            alt={recipe.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-warm-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      ) : (
        <div className={clsx(
          'aspect-[4/3] bg-gradient-to-br flex items-center justify-center relative overflow-hidden',
          getPlaceholderGradient(recipe.title)
        )}>
          <span className="text-6xl drop-shadow-sm transform group-hover:scale-110 transition-transform duration-300">
            {getPlaceholderEmoji(recipe.course, recipe.cuisine)}
          </span>
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} />
        </div>
      )}
      
      <div className="p-4">
        <h3 className="font-display font-semibold text-ink text-lg mb-2 line-clamp-2 group-hover:text-coral-600 transition-colors">
          {recipe.title}
        </h3>
        
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {totalTime > 0 && (
            <span className="badge badge-warm">
              ⏱️ {totalTime}m
            </span>
          )}
          {recipe.servings && (
            <span className="badge badge-warm">
              👥 {recipe.servings}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {recipe.course && (
            <span className="badge badge-primary capitalize">
              {recipe.course}
            </span>
          )}
          {recipe.cuisine && (
            <span className="badge badge-sage">
              {recipe.cuisine}
            </span>
          )}
        </div>
        
        {recipe.createdByName && (
          <div className="mt-3 text-xs text-stone">
            by {recipe.createdByName}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-sand">
          <span className="text-coral-600 text-sm font-medium group-hover:text-coral-700">
            View Recipe →
          </span>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="px-3 py-1.5 text-sm text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
